import type { StatusType } from "manager/components/app-summary/AppListV2/StatusIcon";
import {
  RECOMMENDATION_LABELS,
  resolveVero1RecommendationName,
  Vero1BannerState,
  Vero1ChecklistStatus,
  Vero1Recommendation,
} from "shared/interfaces/api/vero1";
import { bannerDate } from "shared/utils/bannerDate";

export type BannerTone = "hold" | "approve" | "conditional" | "decline" | "cancel" | "error";

export interface ResolvedVero1Banner {
  label: string;
  tone: BannerTone;
  // the verdict's on-dark colour for the banner, matching the Lumen header this
  // replaces; null = uncoloured (renders white via the invert class).
  verdictColor: string | null;
  subtext?: string;
  hideLumenFromViewer: boolean;
  showRecommendation: boolean;
  // hold states, analyst viewer only: the internal-only band (Lumen run
  // provenance + Start VERO1 review) renders in the banner strip (spec §01).
  showInternalHold: boolean;
  // a terminal disposition (the review-less identity close, PRODUCT-2500):
  // not a queue state — never masked by the awaiting-applicants strip
  terminal?: boolean;
  // banner caption override; the shell defaults to "Recommendation"
  eyebrow?: string;
}

const TONE_TO_VERDICT_COLOR: Record<BannerTone, string | null> = {
  approve: "#67D6A6", // .lg-val APPROVE
  conditional: "#F0BE7A", // .lg-val CONDITIONAL
  decline: "#F0728C", // .lg-val DECLINE
  cancel: null, // muted #C8C3DC via shell default
  hold: null,
  error: "#F0728C", // a failed derive reads as an error to the agent, in the decline red
};

const RECOMMENDATION_TONES: Record<string, BannerTone> = {
  [Vero1Recommendation.Approve]: "approve",
  [Vero1Recommendation.Conditional]: "conditional",
  [Vero1Recommendation.Decline]: "decline",
  // Cancel is a process outcome — no verdict colour (spec §01).
  [Vero1Recommendation.Cancel]: "cancel",
};

// Verdict colour for a raw recommendation value — shared by the banner slots
// and the amended-by-VERO1 disclosure row (spec §01).
export function recommendationVerdictColor(
  rec: string | null | undefined
): string | null {
  const tone = rec ? RECOMMENDATION_TONES[rec] : undefined;
  return tone ? TONE_TO_VERDICT_COLOR[tone] : null;
}

// null = not VERO1-governed; caller renders raw Lumen.
export function resolveVero1Banner(
  bannerState: Vero1BannerState | null | undefined,
  isAgent: boolean
): ResolvedVero1Banner | null {
  if (!bannerState) return null;

  if (bannerState.state === "unable_to_verify_identity") {
    return {
      label: "Unable to verify identity",
      eyebrow: "Application Cancelled",
      tone: "cancel",
      verdictColor: null,
      subtext: "Closed — the Identity Risk Check could not be completed.",
      hideLumenFromViewer: !isAgent,
      showRecommendation: false,
      showInternalHold: false,
      terminal: true,
    };
  }

  if (bannerState.state === "recommendation") {
    const rec = bannerState.recommendation;
    const tone: BannerTone = (rec ? RECOMMENDATION_TONES[rec] : undefined) ?? "hold";
    const when = bannerDate(bannerState.submittedAt);
    return {
      // the client's authored word wins; the enum label only carries reviews
      // with no configured name (and CANCEL, which has no severity)
      label:
        resolveVero1RecommendationName(bannerState) ??
        (rec && RECOMMENDATION_LABELS[rec]) ??
        "Reviewed",
      tone,
      verdictColor: TONE_TO_VERDICT_COLOR[tone],
      subtext: when ? `VERO1 review submitted ${when}.` : "VERO1 review submitted.",
      hideLumenFromViewer: !isAgent,
      showRecommendation: true,
      showInternalHold: false,
    };
  }

  // a submitted review whose derived run never landed — the automated score
  // still governs, so no VERO1 verdict shows.
  if (bannerState.state === "override_not_applied") {
    const when = bannerDate(bannerState.submittedAt);
    // leasing reads an ordinary hold. The agent gets an error, not a hold
    // (Kelly, 2026-09-14): their submitted review is NOT governing and the
    // automated result still is — the banner must say so plainly
    if (isAgent) {
      return {
        label: "Review not applied",
        eyebrow: "Error",
        tone: "error",
        verdictColor: TONE_TO_VERDICT_COLOR.error,
        subtext: `The VERO1 review submitted${
          when ? ` ${when}` : ""
        } could not be applied to the screening result, so the automated result is still governing and leasing sees a hold. Re-run screening from the Deal review tab to apply it; if that fails too, contact support.`,
        hideLumenFromViewer: false,
        showRecommendation: false,
        showInternalHold: true,
      };
    }
    return {
      label: "Pending review",
      tone: "hold",
      verdictColor: null,
      subtext: "Queued for VERO1 analyst review",
      hideLumenFromViewer: true,
      showRecommendation: false,
      showInternalHold: false,
    };
  }

  // an applicant added after submission reopens the household: the submitted
  // review is not governing until every applicant has submitted and screening
  // has read them all. Reads as the ordinary hold; the awaiting-applicants
  // strip carries the cause, since the deal is no longer submitted.
  if (bannerState.state === "awaiting_applicants") {
    const when = bannerDate(bannerState.submittedAt);
    return {
      label: "Pending review",
      tone: "hold",
      verdictColor: null,
      subtext: isAgent
        ? `Review submitted${
            when ? ` ${when}` : ""
          } — an applicant was added since. It governs again once every applicant has submitted and screening has read the household.`
        : "Queued for VERO1 analyst review",
      hideLumenFromViewer: !isAgent,
      showRecommendation: false,
      showInternalHold: isAgent,
    };
  }

  if (bannerState.state === "under_review") {
    const when = bannerDate(bannerState.startedAt);
    return {
      label: "Under review",
      tone: "hold",
      verdictColor: null,
      subtext: when
        ? `VERO1 analyst review started ${when}.`
        : "VERO1 analyst review started.",
      hideLumenFromViewer: !isAgent,
      showRecommendation: false,
      showInternalHold: isAgent,
    };
  }

  // pending_review
  return {
    label: "Pending review",
    tone: "hold",
    verdictColor: null,
    subtext: "Queued for VERO1 analyst review",
    hideLumenFromViewer: !isAgent,
    showRecommendation: false,
    showInternalHold: isAgent,
  };
}

export interface FactorCardDisplay {
  showCards: boolean;
}

export function resolveFactorCardDisplay(
  bannerState: Vero1BannerState | null | undefined,
  vero1Active: boolean,
  isAgent: boolean
): FactorCardDisplay {
  // non-VERO1 deals are unaffected — cards render as they do today.
  if (!vero1Active) return { showCards: true };

  const banner = resolveVero1Banner(bannerState, isAgent);
  // no resolvable VERO1 state — treat as normal (don't hide the portal's cards
  // on a deal we can't classify).
  if (!banner) return { showCards: true };

  // recommendation submitted: cards show to everyone (current record).
  if (banner.showRecommendation) {
    return { showCards: true };
  }

  // hold state: analysts see the run cards; leasing sees no cards.
  return { showCards: isAgent };
}

export type Vero1CardVerdict =
  | { mode: "adopt" }
  | { mode: "override"; status: StatusType }
  | { mode: "neutral" };

const NEUTRAL: Vero1CardVerdict = { mode: "neutral" };

const SUBMITTED_STATUSES = new Set<Vero1ChecklistStatus | null>([
  Vero1ChecklistStatus.Submitted,
]);

export const vero1ReviewGoverns = (
  checklistStatus?: Vero1ChecklistStatus | null
): boolean => SUBMITTED_STATUSES.has(checklistStatus ?? null);

// holds including NeedsReReview stay coloured; SUBMITTED colouring comes from
// verdict/governs, not this flag
export const vero1CardNeutral = (
  vero1Active: boolean,
  isVero1Agent: boolean
): boolean => vero1Active && !isVero1Agent;

const RECOMMENDATION_CARD_STATUS: Record<string, StatusType> = {
  [Vero1Recommendation.Approve]: "pass",
  [Vero1Recommendation.Conditional]: "warn",
  [Vero1Recommendation.Decline]: "fail",
};

export function vero1CardVerdict(
  bannerState: Vero1BannerState | null | undefined,
  checklistStatus?: Vero1ChecklistStatus | null
): Vero1CardVerdict {
  if (!bannerState) return NEUTRAL;
  if (!vero1ReviewGoverns(checklistStatus)) return NEUTRAL;
  if (bannerState.state !== "recommendation") return NEUTRAL;

  const rec = bannerState.recommendation;
  if (rec === Vero1Recommendation.Agree) return { mode: "adopt" };

  const status = rec ? RECOMMENDATION_CARD_STATUS[rec] : undefined;
  return status ? { mode: "override", status } : NEUTRAL;
}
